import Button from '@/components/Button';
import PageHeader from '@/components/PageHeader';
import RetailerSelectBox from '@/components/RetailerSelectBox';
import SearchForm from '@/components/search/SearchForm';
import useSearch from '@/components/search/useSearch';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import StoreTable from '@/modules/admin/components/StoreTable';
import { RetailerStoreAddPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { HomePath } from '@/routing/paths';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface Store {
  id: string;
  organizationId: string;
  retailerId: string;
}

type Id = { id: string };

const StoreList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { searchQuery, page, handleSearchSubmit, handlePageChange, setSearchQuery } = useSearch();

  const { retailerId } = useParams();
  const retailerIdParam = searchParams.get('retailerId');
  const activeRetailerId = retailerId || retailerIdParam || null;

  const { switchContext } = useAuth();

  const handleRetailerSelect = (retailer?: Id | null) => {
    const nextParams = new URLSearchParams(searchParams);

    if (retailer?.id) {
      nextParams.set('retailerId', retailer.id);
    } else {
      nextParams.delete('retailerId');
    }

    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleStoreSelect = ({ id: storeId, retailerId, organizationId }: Store) => {
    void (async () => {
      try {
        await switchContext({
          organizationId,
          retailerId: retailerId,
          storeId,
        });
        navigate(HomePath);
      } catch (err) {
        console.error('Failed to switch context:', err);
      }
    })();
  };

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Stores">
        {retailerId && (
          <Button
            to={RetailerStoreAddPath.withRetailerId(retailerId)}
            icon={<PlusIcon className="h-5 w-5 mr-1" />}
            variant="secondary"
          >
            Create Store
          </Button>
        )}
      </PageHeader>

      <SearchForm
        onSubmit={handleSearchSubmit}
        onChange={setSearchQuery}
        value={searchQuery}
        placeholder="Search by name"
      />

      {!retailerId && (
        <div className="mb-8 sm:w-96 lg:w-80">
          <RetailerSelectBox
            value={activeRetailerId}
            onSelect={handleRetailerSelect}
            showOrganization
          />
        </div>
      )}

      <StoreTable
        searchQuery={searchQuery}
        page={page}
        onChangePage={handlePageChange}
        retailerId={activeRetailerId || undefined}
        onSelectStore={handleStoreSelect}
        onSelectStoreElement={
          <Button
            variant="secondary"
            size="sm"
            icon={<ArrowRightStartOnRectangleIcon className="w-5 h-5" />}
          >
            Enter Store
          </Button>
        }
      />
    </>
  );
};

export default StoreList;
